import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import Button from "../ui/Button";
import { IconLogout } from "../icons/dashboardNavIcons";
import api from "../../utils/api";
import { getImpersonationState, applyAuthResponse } from "../../lib/authSession";
import { applyAccountStatusToSession } from "../../lib/accountStatus";
import { getPostLoginPath } from "../../lib/authRoutes";
import { stopAdminImpersonation } from "../../admin/dashboard/adminApi";
import { useTranslation } from "../../i18n";
import BrandLogo from "../ui/BrandLogo";

const BrandMark = () => <BrandLogo size={36} />;

const PlusIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M12 5V19M5 12H19"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * @typedef {{ label: string; to: string; icon: React.ReactNode; end?: boolean }} DashboardNavItem
 * @typedef {{ label: string; to: string; icon: React.ReactNode }} DashboardFooterItem
 *
 * @param {{
 *   navItems: DashboardNavItem[];
 *   footerItems?: DashboardFooterItem[];
 *   brandTitle?: string;
 *   brandSubtitle?: string;
 *   primaryAction?: { label: string; onClick?: () => void };
 * }} props
 */
const DashboardSidebar = ({
  navItems,
  footerItems = [],
  brandTitle = "ReviewLytics",
  brandSubtitle = "REVIEW ANALYTICS",
  primaryAction = { label: " New Case" },
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      if (getImpersonationState()) {
        const data = await stopAdminImpersonation();
        applyAuthResponse(data);
        if (data?.user) {
          applyAccountStatusToSession(data.user);
        }
        navigate(getPostLoginPath(data?.user?.role || "admin"), { replace: true });
        return;
      }
      await api.logout();
      navigate("/anmelden", { replace: true });
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <aside className="fixed left-0 top-0 z-40 flex h-screen w-[264px] flex-col border-r border-[#ececec] bg-[#F8F9FA] px-4 py-6">
      <div className="mb-8 flex items-start gap-3 px-1">
        <BrandMark />
        <div className="min-w-0 pt-0.5">
          <p className="text-[15px] font-semibold leading-tight tracking-[-0.02em] text-[#0f172a]">
            {brandTitle}
          </p>
          <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#94a3b8]">
            {brandSubtitle}
          </p>
        </div>
      </div>

      <nav className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto" aria-label={t("common.mainNav")}>
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              [
                "flex items-center gap-3 rounded-xl px-3 py-2.5 text-[14px] font-medium transition-colors",
                isActive
                  ? "bg-white text-[#0f172a] shadow-[0_2px_8px_rgba(15,23,42,0.06)]"
                  : "text-[#64748b] hover:bg-white/60 hover:text-[#334155]",
              ].join(" ")
            }
          >
            <span className="flex h-5 w-5 shrink-0 items-center justify-center [&>svg]:shrink-0">
              {item.icon}
            </span>
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="mt-4 shrink-0">
        <Button
          type="button"
          showArrow={false}
          className="!h-12 !rounded-full !text-[14px]"
          onClick={primaryAction.onClick}
        >
          <span className="inline-flex items-center gap-2">
            <PlusIcon />
            {primaryAction.label}
          </span>
        </Button>
      </div>

      <div className="mt-auto flex flex-col gap-1 border-t border-[#e8eaed] pt-5">
        {footerItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className="flex items-center gap-3 rounded-xl px-3 py-2 text-[14px] font-medium text-[#64748b] transition-colors hover:bg-white/60 hover:text-[#334155]"
          >
            <span className="flex h-5 w-5 shrink-0 items-center justify-center [&>svg]:shrink-0">
              {item.icon}
            </span>
            {item.label}
          </NavLink>
        ))}
        <button
          type="button"
          onClick={handleLogout}
          disabled={isLoggingOut}
          className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-[14px] font-medium text-[#64748b] transition-colors hover:bg-white/60 hover:text-[#b91c1c] disabled:cursor-not-allowed disabled:opacity-60"
        >
          <span className="flex h-5 w-5 shrink-0 items-center justify-center [&>svg]:shrink-0">
            <IconLogout />
          </span>
          {isLoggingOut
            ? t("sidebar.loggingOut")
            : getImpersonationState()
              ? t("sidebar.backToAdmin")
              : t("sidebar.logout")}
        </button>
      </div>
    </aside>
  );
};

export default DashboardSidebar;
